import { useMemo, useState } from "react";
import { Loader2, ListPlus, Square, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  useContactFollowUpSequences,
  type ContactFollowUpState,
} from "@/hooks/useContactFollowUpSequences";
import { FOLLOW_UP_STATUS_LABELS, isFollowUpStateLive } from "@/lib/followUpStatus";
import type { Database } from "@/lib/supabase";

type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

interface FollowUpSequenceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: AccountContact | null;
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const meta = FOLLOW_UP_STATUS_LABELS[status];
  if (!meta) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-neutral-500/15 dark:text-neutral-300">
        {status}
      </span>
    );
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${meta.className}`}>{t(meta.labelKey)}</span>
  );
}

export function FollowUpSequenceModal({
  open,
  onOpenChange,
  contact,
}: FollowUpSequenceModalProps) {
  const { t } = useTranslation("chat");
  const { sequences, availableSequences, loading, enroll, stop } =
    useContactFollowUpSequences(contact?.id);

  const [selectedSequenceId, setSelectedSequenceId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [pendingStopId, setPendingStopId] = useState<string | null>(null);
  // Inclusão manual liga uma cadência automática numa conversa possivelmente
  // atendida por humano: exige confirmação explícita.
  const [isConfirming, setIsConfirming] = useState(false);

  const liveSequences = useMemo(
    () => sequences.filter((item) => isFollowUpStateLive(item.status)),
    [sequences]
  );

  // A guarda anti-spam do motor só morde ENTRE SEQUÊNCIAS DIFERENTES: uma cadência
  // não dispara se outra já enviou follow-up a este contato nas últimas 20h ou tem
  // mensagem na fila. Dentro da mesma sequência não há limite.
  //
  // Por isso o aviso olha apenas inscrições realmente armadas ('active' com disparo
  // calculado). Contar 'blocked'/'paused' aqui deixaria o aviso permanente: o motor
  // inscreve todo contato com assistente ativo em todas as sequências daquele
  // assistente, e essas linhas ficam em 'blocked' — sem nunca enviar nada.
  const hasArmedSequence = useMemo(
    () =>
      sequences.some((item) => item.status === "active" && !!item.next_fire_at),
    [sequences]
  );

  const selectableSequences = useMemo(
    () =>
      availableSequences.filter(
        (sequence) =>
          !liveSequences.some((state) => state.follow_up_id === sequence.id)
      ),
    [availableSequences, liveSequences]
  );

  const handleAdd = async () => {
    if (!selectedSequenceId) return;
    setIsSaving(true);
    const result = await enroll(selectedSequenceId);
    setIsSaving(false);

    if (result.ok) {
      toast.success(t("followUpSequence.added"));
      setSelectedSequenceId("");
      setIsConfirming(false);
    } else {
      toast.error(result.error ?? t("followUpSequence.addError"));
    }
  };

  const selectedSequenceName = availableSequences.find(
    (item) => item.id === selectedSequenceId
  )?.name;

  const handleOpenChange = (next: boolean) => {
    if (!next) setIsConfirming(false);
    onOpenChange(next);
  };

  const handleStop = async (state: ContactFollowUpState) => {
    setPendingStopId(state.id);
    const result = await stop(state.id);
    setPendingStopId(null);

    if (result.ok) {
      toast.success(t("followUpSequence.stopped"));
    } else {
      toast.error(result.error ?? t("followUpSequence.stopError"));
    }
  };

  if (isConfirming) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{t("followUpSequence.confirmTitle")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm">
              {t("followUpSequence.confirmQuestion", {
                name: selectedSequenceName ?? "",
              })}
            </p>

            <div className="flex items-start gap-2 rounded-[10px] border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="space-y-2 text-xs text-amber-800 dark:text-amber-200">
                <p>{t("followUpSequence.confirmWarningAuto")}</p>
                <p>{t("followUpSequence.confirmWarningOverlap")}</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setIsConfirming(false)}
              disabled={isSaving}
            >
              {t("followUpSequence.confirmBack")}
            </Button>
            <Button onClick={handleAdd} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : (
                <ListPlus className="w-4 h-4 mr-1.5" />
              )}
              {t("followUpSequence.confirmYes")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t("followUpSequence.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label>{t("followUpSequence.current")}</Label>

            {loading && sequences.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("followUpSequence.loading")}
              </div>
            ) : sequences.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("followUpSequence.none")}
              </p>
            ) : (
              <ul className="space-y-2">
                {sequences.map((state) => (
                  <li
                    key={state.id}
                    className="flex items-center justify-between gap-3 rounded-[10px] border border-border p-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm truncate">
                        {state.follow_up?.name ?? t("followUpSequence.unnamed")}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <StatusBadge status={state.status} />
                        {state.enrollment_source === "manual" && (
                          <span className="text-[11px] text-muted-foreground">
                            {t("followUpSequence.manualBadge")}
                          </span>
                        )}
                      </div>
                    </div>

                    {isFollowUpStateLive(state.status) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStop(state)}
                        disabled={pendingStopId === state.id}
                      >
                        {pendingStopId === state.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Square className="w-3.5 h-3.5 mr-1.5" />
                            {t("followUpSequence.stop")}
                          </>
                        )}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="followup-sequence-select">
              {t("followUpSequence.add")}
            </Label>

            {selectableSequences.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("followUpSequence.noneAvailable")}
              </p>
            ) : (
              <>
                <Select
                  value={selectedSequenceId}
                  onValueChange={setSelectedSequenceId}
                >
                  <SelectTrigger id="followup-sequence-select">
                    <SelectValue placeholder={t("followUpSequence.pick")} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableSequences.map((sequence) => (
                      <SelectItem key={sequence.id} value={sequence.id}>
                        {sequence.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasArmedSequence && (
                  <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    {t("followUpSequence.antiSpamWarning")}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t("followUpSequence.close")}
          </Button>
          <Button
            onClick={() => setIsConfirming(true)}
            disabled={!selectedSequenceId}
          >
            <ListPlus className="w-4 h-4 mr-1.5" />
            {t("followUpSequence.confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FollowUpSequenceModal;
